import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Plus, Printer, RotateCcw, Trash2, Copy } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { NumberInput } from "@/components/calculator/NumberInput";
import { Button } from "@/components/ui/button";

import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { formatBDT } from "@/lib/bdCurrency";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  BD_VAT_RATES,
  BD_VAT_SECTORS,
  VAT_LAST_UPDATED,
  NBR_VAT_URL,
} from "@/data/bdVatRates";
import {
  computeInvoice,
  computeVat,
  type LineItem,
  type VatDirection,
} from "@/lib/calculators/vat";

type B = { en: string; bn: string };

const T = {
  h1: { en: "Bangladesh VAT Calculator", bn: "ভ্যাট ক্যালকুলেটর" },
  desc: {
    en: "Add or remove Bangladesh VAT at standard 15% and truncated rates (10%, 7.5%, 5%, 2.4%) with sector presets and a multi-rate invoice builder.",
    bn: "১৫% সাধারণ ও ট্রাঙ্কেটেড হারে (১০%, ৭.৫%, ৫%, ২.৪%) বাংলাদেশের ভ্যাট হিসাব করুন — খাতভিত্তিক প্রিসেট ও ইনভয়েস বিল্ডারসহ।",
  },
  direction: { en: "Calculation direction", bn: "হিসাবের ধরন" },
  add: { en: "Add VAT (Net → Gross)", bn: "ভ্যাট যোগ করুন (নেট → গ্রস)" },
  remove: { en: "Remove VAT (Gross → Net)", bn: "ভ্যাট বাদ দিন (গ্রস → নেট)" },
  amount: { en: "Amount", bn: "পরিমাণ" },
  amountAdd: { en: "Net amount (excluding VAT)", bn: "নেট পরিমাণ (ভ্যাট ছাড়া)" },
  amountRemove: { en: "Gross amount (including VAT)", bn: "গ্রস পরিমাণ (ভ্যাটসহ)" },
  rate: { en: "VAT rate", bn: "ভ্যাট হার" },
  custom: { en: "Custom rate", bn: "কাস্টম হার" },
  customRate: { en: "Custom rate (%)", bn: "কাস্টম হার (%)" },
  sector: { en: "Sector preset", bn: "খাতভিত্তিক প্রিসেট" },
  sectorHint: { en: "Tap to auto-fill the rate", bn: "হার সয়ংক্রিয় করতে চাপুন" },
  net: { en: "Net amount", bn: "নেট পরিমাণ" },
  vat: { en: "VAT amount", bn: "ভ্যাট" },
  gross: { en: "Gross amount", bn: "গ্রস (মোট)" },
  reset: { en: "Reset", bn: "রিসেট" },
  bnDigits: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },
  receipt: { en: "Receipt", bn: "রসিদ" },
  print: { en: "Print", bn: "প্রিন্ট" },
  copyLine: { en: "Copy as invoice line", bn: "ইনভয়েস লাইন কপি" },
  multiTitle: { en: "Multi-rate invoice (advanced)", bn: "একাধিক হারের ইনভয়েস (অ্যাডভান্সড)" },
  multiHint: {
    en: "Add line items with different VAT rates and get a combined invoice total.",
    bn: "বিভিন্ন ভ্যাট হারে লাইন যোগ করে মোট ইনভয়েস বের করুন।",
  },
  addLine: { en: "Add line", bn: "লাইন যোগ" },
  label: { en: "Label", bn: "বিবরণ" },
  totalNet: { en: "Total net", bn: "মোট নেট" },
  totalVat: { en: "Total VAT", bn: "মোট ভ্যাট" },
  totalGross: { en: "Total gross", bn: "মোট গ্রস" },
  remove2: { en: "Remove", bn: "মুছুন" },
} as const;

const FAQ: { q: B; a: B }[] = [
  {
    q: { en: "What is VAT in Bangladesh?", bn: "বাংলাদেশে ভ্যাট কী?" },
    a: {
      en: "Value Added Tax (VAT) is an indirect consumption tax administered by the National Board of Revenue (NBR) under the VAT and Supplementary Duty Act 2012. The standard rate is 15%, with several truncated rates for specific sectors.",
      bn: "মূল্য সংযোজন কর (ভ্যাট) এনবিআর কর্তৃক পরিচালিত একটি পরোক্ষ ভোগ-কর — ২০১২ সালের ভ্যাট ও সম্পূরক শুল্ক আইনের অধীনে। সাধারণ হার ১৫%, কিছু খাতে ট্রাঙ্কেটেড হার রয়েছে।",
    },
  },
  {
    q: { en: "How to calculate VAT?", bn: "ভ্যাট কীভাবে গণনা করা হয়?" },
    a: {
      en: "To add VAT: VAT = Net × (Rate ÷ 100); Gross = Net + VAT. To extract VAT from a gross figure: Net = Gross ÷ (1 + Rate ÷ 100); VAT = Gross − Net. Example at 15%: ৳1,000 net → ৳150 VAT → ৳1,150 gross.",
      bn: "যোগ করতে: ভ্যাট = নেট × (হার ÷ ১০০); গ্রস = নেট + ভ্যাট। গ্রস থেকে বাদ দিতে: নেট = গ্রস ÷ (১ + হার ÷ ১০০); ভ্যাট = গ্রস − নেট। উদাহরণ ১৫%: ৳১,০০০ → ভ্যাট ৳১৫০ → গ্রস ৳১,১৫০।",
    },
  },
  {
    q: { en: "What is truncated VAT?", bn: "ট্রাঙ্কেটেড ভ্যাট কী?" },
    a: {
      en: "Truncated rates are reduced VAT rates set by SRO for specific sectors — e.g., 7.5% for non-AC restaurants and furniture, 5% for MS Rod and IT services, 2.4% for medicine retailers. Businesses on truncated rates generally cannot claim input VAT credit.",
      bn: "ট্রাঙ্কেটেড হার হলো এসআরও দ্বারা নির্দিষ্ট খাতের জন্য নির্ধারিত হ্রাসকৃত ভ্যাট — যেমন নন-এসি রেস্তোরাঁ ও আসবাবপত্রে ৭.৫%, এমএস রড ও আইটিতে ৫%, ঔষধ খুচরায় ২.৪%। ট্রাঙ্কেটেড হারে সাধারণত ইনপুট ভ্যাট রেয়াত পাওয়া যায় না।",
    },
  },
  {
    q: { en: "ভ্যাট কীভাবে গণনা করা হয়?", bn: "ভ্যাট কীভাবে গণনা করা হয়?" },
    a: {
      en: "ভ্যাট = মূল্য × (হার / ১০০)। ১৫% হারে ৳১,০০০ এর ভ্যাট ৳১৫০, মোট ৳১,১৫০।",
      bn: "ভ্যাট = মূল্য × (হার / ১০০)। ১৫% হারে ৳১,০০০ এর ভ্যাট ৳১৫০, মোট ৳১,১৫০।",
    },
  },
  {
    q: { en: "ট্রাঙ্কেটেড ভ্যাট কী?", bn: "ট্রাঙ্কেটেড ভ্যাট কী?" },
    a: {
      en: "ট্রাঙ্কেটেড ভ্যাট হলো নির্দিষ্ট খাতের জন্য কম হারে ভ্যাট আরোপ — সাধারণত ১০%, ৭.৫%, ৫% বা ২.৪%।",
      bn: "ট্রাঙ্কেটেড ভ্যাট হলো নির্দিষ্ট খাতের জন্য কম হারে ভ্যাট আরোপ — সাধারণত ১০%, ৭.৫%, ৫% বা ২.৪%।",
    },
  },
  {
    q: { en: "What is the VAT registration threshold?", bn: "ভ্যাট নিবন্ধনের সীমা কত?" },
    a: {
      en: "Businesses with annual turnover above ৳3 crore must register for VAT and file monthly returns (Mushak 9.1). Smaller businesses (above ৳50 lakh turnover) may need a Turnover Tax certificate at 4%.",
      bn: "বার্ষিক টার্নওভার ৳৩ কোটির বেশি হলে ভ্যাট নিবন্ধন বাধ্যতামূলক এবং মাসিক রিটার্ন (মূসক ৯.১) দাখিল করতে হয়। ৳৫০ লক্ষের বেশি টার্নওভারে ৪% হারে টার্নওভার ট্যাক্স প্রযোজ্য হতে পারে।",
    },
  },
  {
    q: { en: "What is the difference between VAT and Supplementary Duty?", bn: "ভ্যাট ও সম্পূরক শুল্কের পার্থক্য কী?" },
    a: {
      en: "Supplementary Duty (SD) is an additional tax on luxury, harmful or discouraged goods (tobacco, soft drinks, premium cars). SD is calculated first on the price, then VAT 15% is calculated on (price + SD).",
      bn: "সম্পূরক শুল্ক বিলাসী বা ক্ষতিকর পণ্যের (তামাক, কোমল পানীয়, দামি গাড়ি) উপর অতিরিক্ত কর। প্রথমে মূল্যের উপর সম্পূরক শুল্ক, তারপর (মূল্য + শুল্ক) এর উপর ১৫% ভ্যাট হয়।",
    },
  },
  {
    q: { en: "What is a Mushak 6.3 invoice?", bn: "মূসক ৬.৩ চালান কী?" },
    a: {
      en: "Mushak 6.3 is the official VAT invoice/challan that registered businesses must issue for every taxable supply. It must show the seller's BIN, buyer details, item description, value, VAT rate and VAT amount separately.",
      bn: "মূসক ৬.৩ হলো নিবন্ধিত ব্যবসার জন্য বাধ্যতামূলক ভ্যাট চালান। এতে বিক্রেতার BIN, ক্রেতার তথ্য, পণ্যের বিবরণ, মূল্য, ভ্যাট হার ও ভ্যাটের পরিমাণ আলাদাভাবে দেখাতে হয়।",
    },
  },
  {
    q: { en: "When is the VAT return due?", bn: "ভ্যাট রিটার্ন কখন দাখিল করতে হয়?" },
    a: {
      en: "Mushak 9.1 (monthly VAT return) is due by the 15th of the following month. Late filing attracts ৳10,000 monthly penalty plus 2% per month interest on unpaid VAT.",
      bn: "মূসক ৯.১ মাসিক ভ্যাট রিটার্ন পরবর্তী মাসের ১৫ তারিখের মধ্যে দাখিল করতে হয়। দেরিতে ৳১০,০০০ মাসিক জরিমানা ও অপরিশোধিত ভ্যাটের উপর প্রতি মাসে ২% সুদ।",
    },
  },
  {
    q: { en: "Can I claim input VAT credit?", bn: "ইনপুট ভ্যাট রেয়াত পাওয়া যায়?" },
    a: {
      en: "Yes — VAT-registered businesses paying the standard 15% rate can offset VAT paid on purchases (input VAT) against VAT collected from customers (output VAT). Businesses on truncated rates generally cannot.",
      bn: "হ্যাঁ — ১৫% হারে নিবন্ধিত ব্যবসা ক্রয়ের ভ্যাট (ইনপুট) থেকে বিক্রয়ের ভ্যাট (আউটপুট) এর সাথে অফসেট করতে পারে। ট্রাঙ্কেটেড হারে সাধারণত যায় না।",
    },
  },
];

export const Route = createFileRoute("/vat-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "VAT Calculator Bangladesh — 15% Standard & Truncated",
      description:
        "Free Bangladesh VAT calculator with 15%, 10%, 7.5%, 5%, 2.4% truncated rates. Add or remove VAT from invoice. ভ্যাট ক্যালকুলেটর বাংলাদেশ।",
      path: "/vat-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "VAT Calculator", path: "/vat-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: VatCalculatorPage,
});

const CUSTOM = "custom";

function VatCalculatorPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof T) => (T[k] as B)[lang];
  const [bnDigits, setBnDigits] = useState(false);

  const [direction, setDirection] = useState<VatDirection>("add");
  const [amountStr, setAmountStr] = useState("1000");
  const [rateId, setRateId] = useState<string>("standard");
  const [customRateStr, setCustomRateStr] = useState("12");

  const ratePct =
    rateId === CUSTOM
      ? Math.max(0, parseFloat(customRateStr) || 0)
      : (BD_VAT_RATES.find((r) => r.id === rateId)?.ratePct ?? 15);

  const amount = Math.max(0, parseFloat(amountStr) || 0);
  const result = useMemo(() => computeVat(direction, amount, ratePct), [direction, amount, ratePct]);

  const useBn = lang === "bn" || bnDigits;
  const money = (v: number, frac = 2) => formatBDT(Math.round(v * 100) / 100, useBn ? "bn" : "en", frac);
  const num = (n: number | string) => (useBn ? toBengaliNumerals(n) : String(n));

  const reset = () => {
    setDirection("add");
    setAmountStr("1000");
    setRateId("standard");
    setCustomRateStr("12");
  };

  // Multi-rate invoice
  const [items, setItems] = useState<LineItem[]>([
    { id: "l1", label: "Item 1", amount: 1000, ratePct: 15, direction: "add" },
    { id: "l2", label: "Item 2", amount: 500, ratePct: 7.5, direction: "add" },
  ]);
  const invoice = useMemo(() => computeInvoice(items), [items]);

  const updateItem = (id: string, patch: Partial<LineItem>) =>
    setItems((arr) => arr.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  const addItem = () =>
    setItems((arr) => [
      ...arr,
      {
        id: `l${Date.now()}`,
        label: `Item ${arr.length + 1}`,
        amount: 0,
        ratePct: 15,
        direction: "add",
      },
    ]);
  const removeItem = (id: string) => setItems((arr) => arr.filter((it) => it.id !== id));

  const copyInvoiceLine = async () => {
    const text =
      lang === "bn"
        ? `নেট ${money(result.net)} | ভ্যাট (${num(ratePct)}%) ${money(result.vat)} | মোট ${money(result.gross)}`
        : `Net ${money(result.net)} | VAT (${num(ratePct)}%) ${money(result.vat)} | Total ${money(result.gross)}`;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* noop */
    }
  };

  const stackedNetPct = result.gross > 0 ? (result.net / result.gross) * 100 : 0;

  return (
    <CalculatorLayout
      slug="vat-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="ReceiptText"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      {/* Direction toggle */}
      <div className="space-y-2">
        <p className={cn("text-sm font-medium", lang === "bn" && "font-bn")}>{tr("direction")}</p>
        <div className="inline-flex flex-wrap rounded-lg border border-border bg-muted p-0.5">
          {(["add", "remove"] as VatDirection[]).map((d) => {
            const active = direction === d;
            return (
              <button
                key={d}
                type="button"
                onClick={() => setDirection(d)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-sm font-medium transition",
                  active ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                  lang === "bn" && "font-bn",
                )}
              >
                {d === "add" ? tr("add") : tr("remove")}
              </button>
            );
          })}
        </div>
      </div>

      {/* Amount */}
      <div className="mt-6">
        <NumberInput
          label={direction === "add" ? tr("amountAdd") : tr("amountRemove")}
          value={amountStr}
          onChange={setAmountStr}
          unit="৳"
        />
      </div>

      {/* Rate */}
      <div className="mt-6 space-y-2">
        <p className={cn("text-sm font-medium", lang === "bn" && "font-bn")}>{tr("rate")}</p>
        <div className="flex flex-wrap gap-1.5">
          {BD_VAT_RATES.map((r) => {
            const active = rateId === r.id;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setRateId(r.id)}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-xs font-medium transition",
                  active
                    ? "border-accent bg-accent text-accent-foreground"
                    : "border-border bg-card text-foreground/80 hover:border-accent/40",
                  lang === "bn" && "font-bn",
                )}
              >
                {r.label[lang]}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setRateId(CUSTOM)}
            className={cn(
              "rounded-full border px-3 py-1.5 text-xs font-medium transition",
              rateId === CUSTOM
                ? "border-accent bg-accent text-accent-foreground"
                : "border-border bg-card text-foreground/80 hover:border-accent/40",
              lang === "bn" && "font-bn",
            )}
          >
            {tr("custom")}
          </button>
        </div>
        {rateId === CUSTOM && (
          <div className="mt-3 max-w-xs">
            <NumberInput label={tr("customRate")} value={customRateStr} onChange={setCustomRateStr} unit="%" />
          </div>
        )}
      </div>

      {/* Sector presets */}
      <div className="mt-6 space-y-2">
        <p className={cn("text-sm font-medium", lang === "bn" && "font-bn")}>
          {tr("sector")} <span className="ml-1 text-xs font-normal text-muted-foreground">({tr("sectorHint")})</span>
        </p>
        <div className="flex flex-wrap gap-1.5">
          {BD_VAT_SECTORS.map((s) => {
            const matchedRate = BD_VAT_RATES.find((r) => r.ratePct === s.ratePct);
            const active = matchedRate ? rateId === matchedRate.id : false;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  if (matchedRate) setRateId(matchedRate.id);
                  else {
                    setRateId(CUSTOM);
                    setCustomRateStr(String(s.ratePct));
                  }
                }}
                className={cn(
                  "rounded-md border px-2.5 py-1 text-xs transition",
                  active
                    ? "border-accent/50 bg-accent/10 text-accent"
                    : "border-border bg-card text-foreground/70 hover:border-accent/40 hover:text-foreground",
                  lang === "bn" && "font-bn",
                )}
                title={s.note[lang]}
              >
                {s.name[lang]} · {num(s.ratePct)}%
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between gap-3">
        <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <input
            type="checkbox"
            checked={bnDigits}
            onChange={(e) => setBnDigits(e.target.checked)}
            className="h-3.5 w-3.5 accent-accent"
          />
          {tr("bnDigits")}
        </label>
        <Button variant="outline" onClick={reset} className="gap-2">
          <RotateCcw size={14} />
          {tr("reset")}
        </Button>
      </div>

      {/* Results */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mt-8 space-y-6"
      >
        <div className="grid gap-3 md:grid-cols-3">
          <ResultCard label={tr("net")} value={money(result.net)} />
          <ResultCard label={`${tr("vat")} (${num(ratePct)}%)`} value={money(result.vat)} accent />
          <ResultCard label={tr("gross")} value={money(result.gross)} />
        </div>

        {/* Stacked bar */}
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="mb-2 flex justify-between text-xs text-muted-foreground">
            <span className={cn(lang === "bn" && "font-bn")}>{tr("net")}: {money(result.net)}</span>
            <span className={cn(lang === "bn" && "font-bn")}>{tr("vat")}: {money(result.vat)}</span>
          </div>
          <div className="flex h-3 overflow-hidden rounded-full bg-muted">
            <div className="bg-muted-foreground/60" style={{ width: `${stackedNetPct}%` }} />
            <div className="bg-accent" style={{ width: `${100 - stackedNetPct}%` }} />
          </div>
        </div>

        {/* Receipt */}
        <div className="rounded-xl border border-border bg-card p-4 print:border-0 print:shadow-none" id="vat-receipt">
          <div className="mb-3 flex items-center justify-between">
            <h3 className={cn("text-sm font-semibold", lang === "bn" && "font-bn")}>{tr("receipt")}</h3>
            <div className="flex gap-2 print:hidden">
              <Button size="sm" variant="outline" onClick={copyInvoiceLine} className="gap-1.5">
                <Copy size={14} /> {tr("copyLine")}
              </Button>
              <Button size="sm" variant="outline" onClick={() => window.print()} className="gap-1.5">
                <Printer size={14} /> {tr("print")}
              </Button>
            </div>
          </div>
          <div className="rounded-md bg-muted/40 p-4 font-mono text-sm tabular-nums">
            <div className="flex justify-between">
              <span>{tr("net")}</span>
              <span>{money(result.net)}</span>
            </div>
            <div className="flex justify-between">
              <span>{tr("vat")} ({num(ratePct)}%)</span>
              <span>{money(result.vat)}</span>
            </div>
            <div className="my-2 border-t border-dashed border-border" />
            <div className="flex justify-between font-semibold">
              <span>{tr("gross")}</span>
              <span>{money(result.gross)}</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Multi-rate invoice */}
      <details className="mt-10 rounded-xl border border-border bg-card">
        <summary className={cn("cursor-pointer select-none px-4 py-3 text-sm font-semibold", lang === "bn" && "font-bn")}>
          {tr("multiTitle")}
        </summary>
        <div className="space-y-4 border-t border-border p-4">
          <p className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{tr("multiHint")}</p>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className={cn("px-2 py-2 text-left", lang === "bn" && "font-bn")}>{tr("label")}</th>
                  <th className={cn("px-2 py-2 text-right", lang === "bn" && "font-bn")}>{tr("amount")}</th>
                  <th className={cn("px-2 py-2 text-right", lang === "bn" && "font-bn")}>{tr("rate")} %</th>
                  <th className={cn("px-2 py-2 text-center", lang === "bn" && "font-bn")}>{tr("direction")}</th>
                  <th className={cn("px-2 py-2 text-right", lang === "bn" && "font-bn")}>{tr("vat")}</th>
                  <th className={cn("px-2 py-2 text-right", lang === "bn" && "font-bn")}>{tr("gross")}</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {invoice.lines.map((l) => (
                  <tr key={l.id} className="border-t border-border align-middle">
                    <td className="px-2 py-1.5">
                      <input
                        value={l.label}
                        onChange={(e) => updateItem(l.id, { label: e.target.value })}
                        className="w-32 rounded-md border border-border bg-background px-2 py-1 text-sm"
                      />
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <input
                        type="number"
                        value={l.amount}
                        min={0}
                        onChange={(e) => updateItem(l.id, { amount: parseFloat(e.target.value) || 0 })}
                        className="w-28 rounded-md border border-border bg-background px-2 py-1 text-right text-sm tabular-nums"
                      />
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <input
                        type="number"
                        value={l.ratePct}
                        min={0}
                        step={0.1}
                        onChange={(e) => updateItem(l.id, { ratePct: parseFloat(e.target.value) || 0 })}
                        className="w-20 rounded-md border border-border bg-background px-2 py-1 text-right text-sm tabular-nums"
                      />
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <select
                        value={l.direction}
                        onChange={(e) => updateItem(l.id, { direction: e.target.value as VatDirection })}
                        className="rounded-md border border-border bg-background px-2 py-1 text-xs"
                      >
                        <option value="add">+ VAT</option>
                        <option value="remove">− VAT</option>
                      </select>
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{money(l.vat)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{money(l.gross)}</td>
                    <td className="px-2 py-1.5 text-right">
                      <button
                        type="button"
                        onClick={() => removeItem(l.id)}
                        className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        aria-label={tr("remove2")}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border bg-muted/40 text-sm font-semibold">
                  <td className={cn("px-2 py-2", lang === "bn" && "font-bn")} colSpan={4}>
                    {tr("totalNet")} / {tr("totalVat")} / {tr("totalGross")}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">{money(invoice.totalVat)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{money(invoice.totalGross)}</td>
                  <td />
                </tr>
                <tr className="text-xs text-muted-foreground">
                  <td className="px-2 py-1" colSpan={3}>
                    {tr("totalNet")}: {money(invoice.totalNet)}
                  </td>
                  <td colSpan={4} />
                </tr>
              </tfoot>
            </table>
          </div>

          <Button variant="outline" size="sm" onClick={addItem} className="gap-1.5">
            <Plus size={14} /> {tr("addLine")}
          </Button>
        </div>
      </details>

      {/* Disclaimer */}
      <p className={cn("mt-6 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
        {lang === "en"
          ? `Rates as per VAT and Supplementary Duty Act 2012. Last updated ${VAT_LAST_UPDATED}. Verify current rates with `
          : `হার ২০১২ সালের ভ্যাট ও সম্পূরক শুল্ক আইন অনুযায়ী। সর্বশেষ আপডেট ${VAT_LAST_UPDATED}। বর্তমান হার যাচাই করুন `}
        <a href={NBR_VAT_URL} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
          NBR
        </a>
        .
      </p>

      {/* Educational content */}
      <article className="mt-12 space-y-10">
        <Section title={lang === "en" ? "What is VAT in Bangladesh?" : "বাংলাদেশে ভ্যাট কী?"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "VAT is a consumption tax administered by the National Board of Revenue (NBR) under the VAT and Supplementary Duty Act 2012. It is collected at every stage of the supply chain by registered businesses (BIN holders) and ultimately borne by the final consumer."
              : "ভ্যাট হলো এনবিআর কর্তৃক ২০১২ সালের ভ্যাট ও সম্পূরক শুল্ক আইনের অধীনে পরিচালিত একটি ভোগ-কর। এটি সরবরাহ শৃঙ্খলের প্রতিটি ধাপে নিবন্ধিত ব্যবসা (BIN ধারক) সংগ্রহ করে এবং চূড়ান্ত ভোক্তা বহন করে।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Standard 15% vs truncated rates" : "১৫% সাধারণ বনাম ট্রাঙ্কেটেড হার"} lang={lang}>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{tr("sector")}</th>
                  <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("rate")}</th>
                  <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{lang === "en" ? "Note" : "মন্তব্য"}</th>
                </tr>
              </thead>
              <tbody>
                {BD_VAT_SECTORS.map((s) => (
                  <tr key={s.id} className="border-t border-border">
                    <td className={cn("px-3 py-2", lang === "bn" && "font-bn")}>{s.name[lang]}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{num(s.ratePct)}%</td>
                    <td className={cn("px-3 py-2 text-muted-foreground", lang === "bn" && "font-bn")}>{s.note[lang]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title={lang === "en" ? "VAT registration threshold" : "ভ্যাট নিবন্ধনের সীমা"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "Businesses with annual turnover above ৳3 crore must register for VAT and obtain a 13-digit Business Identification Number (BIN). Businesses with turnover above ৳50 lakh but below the VAT threshold may need a Turnover Tax certificate at 4%."
              : "৳৩ কোটির বেশি বার্ষিক টার্নওভারে ভ্যাট নিবন্ধন বাধ্যতামূলক এবং ১৩-অঙ্কের BIN নিতে হয়। ৳৫০ লক্ষের বেশি কিন্তু ভ্যাট সীমার নিচে টার্নওভারে ৪% হারে টার্নওভার ট্যাক্স লাগতে পারে।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "VAT vs Supplementary Duty (SD)" : "ভ্যাট বনাম সম্পূরক শুল্ক"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "Supplementary Duty applies to luxury, harmful or discouraged goods (tobacco, soft drinks, premium cars, cosmetics). SD is calculated first on the assessable value, then VAT 15% is applied on (value + SD). Effective burden therefore stacks."
              : "বিলাসী, ক্ষতিকর বা নিরুৎসাহিত পণ্যে (তামাক, কোমল পানীয়, দামি গাড়ি, কসমেটিকস) সম্পূরক শুল্ক প্রযোজ্য। প্রথমে মূল্যের উপর সম্পূরক শুল্ক, তারপর (মূল্য + শুল্ক) এর উপর ১৫% ভ্যাট হিসাব হয়।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Mushak 6.3 invoice & 9.1 return" : "মূসক ৬.৩ চালান ও ৯.১ রিটার্ন"} lang={lang}>
          <ul className={cn("list-disc space-y-1 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>
              {lang === "en"
                ? "Mushak 6.3: official tax invoice — must show seller BIN, buyer details, item, value, VAT rate and VAT amount separately."
                : "মূসক ৬.৩: সরকারি ভ্যাট চালান — বিক্রেতার BIN, ক্রেতার তথ্য, পণ্য, মূল্য, ভ্যাট হার ও ভ্যাট পরিমাণ আলাদা দেখাতে হয়।"}
            </li>
            <li>
              {lang === "en"
                ? "Mushak 9.1: monthly VAT return, due by 15th of next month."
                : "মূসক ৯.১: মাসিক ভ্যাট রিটার্ন, পরের মাসের ১৫ তারিখের মধ্যে দাখিল।"}
            </li>
            <li>
              {lang === "en"
                ? "Late filing: ৳10,000 fixed penalty + 2% per month interest on unpaid VAT."
                : "দেরিতে দাখিল: ৳১০,০০০ নির্দিষ্ট জরিমানা + অপরিশোধিত ভ্যাটের উপর প্রতি মাসে ২% সুদ।"}
            </li>
            <li>
              {lang === "en"
                ? "Input VAT credit: businesses on standard 15% can offset VAT on purchases against VAT collected."
                : "ইনপুট ভ্যাট রেয়াত: ১৫% হারের ব্যবসা ক্রয়ের ভ্যাট থেকে আউটপুট ভ্যাটে অফসেট করতে পারে।"}
            </li>
          </ul>
        </Section>

        <Section title={lang === "en" ? "Related calculators" : "সম্পর্কিত ক্যালকুলেটর"} lang={lang}>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link to="/tax-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">
              {lang === "en" ? "Income Tax" : "আয়কর"}
            </Link>
            <Link to="/percentage-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">
              {lang === "en" ? "Percentage" : "শতকরা"}
            </Link>
            <Link to="/discount-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">
              {lang === "en" ? "Discount" : "ডিসকাউন্ট"}
            </Link>
          </div>
        </Section>
      </article>

      {/* Print styles */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #vat-receipt, #vat-receipt * { visibility: visible !important; }
          #vat-receipt { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>
    </CalculatorLayout>
  );
}

function ResultCard({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={cn(
        "rounded-xl border p-4",
        accent ? "border-accent/40 bg-accent/5" : "border-border bg-card",
      )}
    >
      <p className={cn("text-xs font-semibold uppercase tracking-wide", accent ? "text-accent" : "text-muted-foreground")}>
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold tabular-nums md:text-3xl">{value}</p>
    </motion.div>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: "en" | "bn" }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}
